import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Layout } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  UsersThree,
  ShieldStar,
  User as UserIcon,
  ArrowClockwise,
  Copy,
  X,
  EnvelopeSimple,
  Check,
  ClockCounterClockwise,
  EyeSlash,
  Prohibit,
  ArrowCounterClockwise,
  Trash,
  MagnifyingGlass,
  Warning,
} from "@phosphor-icons/react";

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [resetInfo, setResetInfo] = useState(null); // { email, reset_link, email_sent }
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState(null); // { email, shared, items, loading }
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // user object

  const load = () => {
    setLoading(true);
    api
      .get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const changeRole = async (u, role) => {
    setBusyId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role } : x)));
      toast.success(`${u.email} is now ${role === "admin" ? "an admin" : "a standard user"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update role");
    } finally {
      setBusyId(null);
    }
  };

  const sendReset = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetInfo(data);
      setCopied(false);
      if (data.email_sent) toast.success(`Reset email dispatched to ${u.email}`);
      else toast.message("Reset link generated (email not configured)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate reset");
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(resetInfo.reset_link);
      setCopied(true);
      toast.success("Reset link copied");
    } catch {
      toast.error("Copy failed — select the link manually");
    }
  };

  const viewHistory = async (u) => {
    setHistory({ email: u.email, shared: u.share_history, items: [], loading: true });
    try {
      const { data } = await api.get(`/admin/users/${u.id}/history`);
      setHistory({ email: data.email, shared: data.shared, items: data.items || [], loading: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load history");
      setHistory(null);
    }
  };

  const toggleSuspend = async (u) => {
    setBusyId(u.id);
    try {
      const next = !u.disabled;
      await api.patch(`/admin/users/${u.id}/status`, { disabled: next });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, disabled: next } : x)));
      toast.success(next ? `${u.email} suspended` : `${u.email} reactivated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update status");
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (u) => {
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(`${u.email} deleted`);
      setConfirmDelete(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete user");
    } finally {
      setBusyId(null);
    }
  };

  const adminCount = users.filter((u) => u.role === "admin").length;
  const q = search.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter((u) => (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q))
    : users;

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6">
        <h1 className="text-2xl tracking-tight flex items-center gap-2">
          <UsersThree size={22} className="text-[#00F0FF]" /> User Management
        </h1>
        <p className="text-sm text-[#A1A1AA] font-mono mt-1">
          Manage accounts · promote to admin · trigger password resets.
        </p>
      </div>

      <div className="max-w-5xl p-8 space-y-6">
        <div className="flex items-center gap-6 font-mono text-xs text-[#71717A]">
          <span data-testid="admin-total-count">
            <span className="text-[#FAFAFA]">{users.length}</span> total users
          </span>
          <span data-testid="admin-admin-count">
            <span className="text-[#00F0FF]">{adminCount}</span> admins
          </span>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input
                data-testid="admin-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email…"
                className="bg-[#050505] border border-[#27272A] pl-8 pr-3 py-1.5 text-xs font-mono text-[#FAFAFA] focus:border-[#00F0FF] focus:outline-none transition-colors duration-200 w-56"
              />
            </div>
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 hover:border-[#00F0FF] hover:text-[#FAFAFA] transition-colors duration-200"
            >
              <ArrowClockwise size={13} /> Refresh
            </button>
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#0A0A0B]" data-testid="admin-users-table">
          <div className="grid grid-cols-[1fr_92px_120px_1fr] gap-4 px-5 py-3 border-b border-[#27272A] font-mono text-[10px] uppercase tracking-wider text-[#71717A]">
            <span>User</span>
            <span>Role</span>
            <span>Usage</span>
            <span className="text-right">Actions</span>
          </div>

          {loading && <div className="px-5 py-8 text-sm text-[#71717A] font-mono">Loading users…</div>}
          {!loading && filteredUsers.length === 0 && (
            <div className="px-5 py-8 text-sm text-[#71717A] font-mono">
              {q ? "No users match your search." : "No users found."}
            </div>
          )}

          {!loading &&
            filteredUsers.map((u) => (
              <div
                key={u.id}
                data-testid={`admin-user-row-${u.email}`}
                className={`grid grid-cols-[1fr_92px_120px_1fr] gap-4 px-5 py-4 border-b border-[#18181B] items-center hover:bg-[#121214] transition-colors duration-150 ${u.disabled ? "opacity-60" : ""}`}
              >
                <div className="min-w-0 flex items-center gap-3">
                  <div className="w-8 h-8 shrink-0 border border-[#27272A] flex items-center justify-center font-mono text-xs text-[#00F0FF]">
                    {(u.name || u.email || "U").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-[#FAFAFA] truncate flex items-center gap-2">
                      {u.name || "—"}
                      {u.is_self && (
                        <span className="font-mono text-[9px] text-[#00FF41] border border-[#00FF41]/40 px-1.5 py-0.5">
                          YOU
                        </span>
                      )}
                      {u.disabled && (
                        <span data-testid={`admin-suspended-badge-${u.email}`} className="font-mono text-[9px] text-[#FF5F56] border border-[#FF5F56]/40 px-1.5 py-0.5">
                          SUSPENDED
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[11px] text-[#71717A] truncate">{u.email}</div>
                    <div className="font-mono text-[10px] text-[#52525B] truncate">joined {fmtDate(u.created_at)}</div>
                  </div>
                </div>

                <div>
                  {u.role === "admin" ? (
                    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#00F0FF] border border-[#00F0FF]/30 px-2 py-1">
                      <ShieldStar size={13} weight="fill" /> admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#A1A1AA] border border-[#27272A] px-2 py-1">
                      <UserIcon size={13} /> user
                    </span>
                  )}
                </div>

                <div className="font-mono text-[11px] leading-relaxed" data-testid={`admin-usage-${u.email}`}>
                  <div className="text-[#FAFAFA]">{u.usage?.gen_total || 0} gens</div>
                  <div className="text-[#71717A]">
                    <span className="text-[#00F0FF]">{u.usage?.gen_builtin || 0}</span> built-in ·{" "}
                    <span className="text-[#00FF41]">{u.usage?.gen_byo || 0}</span> own
                  </div>
                  <div className="text-[#52525B]" title="Estimated tokens in/out">
                    ~{((u.usage?.tokens_in || 0) + (u.usage?.tokens_out || 0)).toLocaleString()} tok
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 flex-wrap">
                  <button
                    data-testid={`admin-history-btn-${u.email}`}
                    onClick={() => viewHistory(u)}
                    title={u.share_history ? "View shared generation history" : "User has not opted in to share history"}
                    className={`flex items-center gap-1.5 font-mono text-[11px] border px-2.5 py-1.5 transition-colors duration-200 ${u.share_history ? "border-[#00FF41]/40 text-[#00FF41] hover:bg-[#00FF41]/10" : "border-[#27272A] text-[#52525B] hover:border-[#71717A]"}`}
                  >
                    <ClockCounterClockwise size={13} /> History
                  </button>
                  {u.role === "admin" ? (
                    <button
                      data-testid={`admin-demote-btn-${u.email}`}
                      disabled={busyId === u.id || u.is_self}
                      onClick={() => changeRole(u, "user")}
                      className="font-mono text-[11px] border border-[#27272A] text-[#A1A1AA] px-2.5 py-1.5 hover:border-[#FF5F56] hover:text-[#FF5F56] transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Demote
                    </button>
                  ) : (
                    <button
                      data-testid={`admin-promote-btn-${u.email}`}
                      disabled={busyId === u.id}
                      onClick={() => changeRole(u, "admin")}
                      className="font-mono text-[11px] border border-[#27272A] text-[#00F0FF] px-2.5 py-1.5 hover:bg-[#00F0FF]/10 transition-colors duration-200 disabled:opacity-40"
                    >
                      Make admin
                    </button>
                  )}
                  <button
                    data-testid={`admin-reset-btn-${u.email}`}
                    disabled={busyId === u.id}
                    onClick={() => sendReset(u)}
                    className="flex items-center gap-1.5 font-mono text-[11px] border border-[#0A84FF] text-[#0A84FF] px-2.5 py-1.5 hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-40"
                  >
                    <EnvelopeSimple size={13} /> Reset PW
                  </button>
                  <button
                    data-testid={`admin-suspend-btn-${u.email}`}
                    disabled={busyId === u.id || u.is_self}
                    onClick={() => toggleSuspend(u)}
                    title={u.is_self ? "You can't suspend yourself" : u.disabled ? "Reactivate account" : "Suspend account (blocks login)"}
                    className={`flex items-center gap-1.5 font-mono text-[11px] border px-2.5 py-1.5 transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed ${u.disabled ? "border-[#00FF41]/40 text-[#00FF41] hover:bg-[#00FF41]/10" : "border-[#FFB020]/40 text-[#FFB020] hover:bg-[#FFB020]/10"}`}
                  >
                    {u.disabled ? <><ArrowCounterClockwise size={13} /> Reactivate</> : <><Prohibit size={13} /> Suspend</>}
                  </button>
                  <button
                    data-testid={`admin-delete-btn-${u.email}`}
                    disabled={busyId === u.id || u.is_self}
                    onClick={() => setConfirmDelete(u)}
                    title={u.is_self ? "You can't delete yourself" : "Delete account permanently"}
                    className="flex items-center gap-1.5 font-mono text-[11px] border border-[#FF5F56]/40 text-[#FF5F56] px-2.5 py-1.5 hover:bg-[#FF5F56]/10 transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash size={13} /> Delete
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            data-testid="admin-delete-modal"
            className="bg-[#0A0A0B] border border-[#FF5F56]/40 w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <Warning size={22} weight="fill" className="text-[#FF5F56] mt-0.5 shrink-0" />
              <div>
                <h2 className="text-lg tracking-tight">Delete this user?</h2>
                <p className="text-[12px] text-[#A1A1AA] font-mono mt-1 leading-relaxed">
                  <span className="text-[#FAFAFA]">{confirmDelete.email}</span> and all of their saved projects &amp; history will be permanently removed. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                data-testid="admin-delete-cancel"
                onClick={() => setConfirmDelete(null)}
                className="font-mono text-xs border border-[#27272A] text-[#A1A1AA] px-4 py-2 hover:text-[#FAFAFA] transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                data-testid="admin-delete-confirm"
                disabled={busyId === confirmDelete.id}
                onClick={() => deleteUser(confirmDelete)}
                className="flex items-center gap-1.5 font-mono text-xs bg-[#FF5F56] text-[#0A0A0B] font-medium px-4 py-2 hover:bg-[#ff4a40] transition-colors duration-200 disabled:opacity-50"
              >
                <Trash size={14} /> {busyId === confirmDelete.id ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetInfo && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setResetInfo(null)}
        >
          <div
            data-testid="admin-reset-modal"
            className="bg-[#0A0A0B] border border-[#27272A] w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-lg tracking-tight flex items-center gap-2">
                <EnvelopeSimple size={18} className="text-[#00F0FF]" /> Password reset
              </h2>
              <button data-testid="admin-reset-modal-close" onClick={() => setResetInfo(null)} className="text-[#71717A] hover:text-[#FAFAFA]">
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-[#A1A1AA] mb-1">
              For <span className="text-[#FAFAFA] font-mono">{resetInfo.email}</span>
            </p>
            <p className="text-[11px] font-mono mb-4">
              {resetInfo.email_sent ? (
                <span className="text-[#00FF41]">✓ Reset email dispatched. </span>
              ) : (
                <span className="text-[#FFB020]">Email delivery not configured. </span>
              )}
              <span className="text-[#71717A]">Or share this one-time link (valid 1 hour):</span>
            </p>
            <div className="flex items-stretch gap-2">
              <input
                data-testid="admin-reset-link"
                readOnly
                value={resetInfo.reset_link}
                className="flex-1 bg-[#050505] border border-[#27272A] px-3 py-2 text-[11px] font-mono text-[#A1A1AA] focus:outline-none"
              />
              <button
                data-testid="admin-copy-link-btn"
                onClick={copyLink}
                className="flex items-center gap-1.5 bg-[#00F0FF] text-[#0A0A0B] font-medium px-4 text-sm hover:bg-[#00d4e0] transition-colors duration-200"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}
      {history && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setHistory(null)}
        >
          <div
            data-testid="admin-history-modal"
            className="bg-[#0A0A0B] border border-[#27272A] w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-6 border-b border-[#27272A]">
              <div>
                <h2 className="text-lg tracking-tight flex items-center gap-2">
                  <ClockCounterClockwise size={18} className="text-[#00F0FF]" /> Generation history
                </h2>
                <p className="text-[11px] font-mono text-[#71717A] mt-1">{history.email}</p>
              </div>
              <button data-testid="admin-history-modal-close" onClick={() => setHistory(null)} className="text-[#71717A] hover:text-[#FAFAFA]">
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto p-6 space-y-4">
              {history.loading && <p className="text-sm text-[#71717A] font-mono">Loading…</p>}

              {!history.loading && !history.shared && (
                <div className="flex items-start gap-3 border border-[#27272A] bg-[#0f0f11] p-5" data-testid="admin-history-not-shared">
                  <EyeSlash size={18} className="text-[#FFB020] mt-0.5 shrink-0" />
                  <p className="text-[12px] text-[#A1A1AA] font-mono leading-relaxed">
                    This user hasn't opted in to share their generation history. They can enable
                    "Share history for support" in their Settings to let you assist them.
                  </p>
                </div>
              )}

              {!history.loading && history.shared && history.items.length === 0 && (
                <p className="text-sm text-[#71717A] font-mono" data-testid="admin-history-empty">
                  Sharing is on, but no generations have been recorded yet.
                </p>
              )}

              {!history.loading &&
                history.shared &&
                history.items.map((it) => (
                  <div key={it.id} className="border border-[#27272A] bg-[#0f0f11]" data-testid="admin-history-item">
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#27272A] font-mono text-[10px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[#00F0FF] uppercase">{it.output_type}</span>
                        <span className="text-[#71717A]">{it.provider} · {it.model}</span>
                        <span className={it.used_own_key ? "text-[#00FF41]" : "text-[#0A84FF]"}>
                          {it.used_own_key ? "own key" : "built-in"}
                        </span>
                      </div>
                      <span className="text-[#52525B]">{fmtDate(it.created_at)}</span>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      <div>
                        <div className="font-mono text-[9px] uppercase tracking-wider text-[#52525B] mb-1">Prompt</div>
                        <p className="text-[12px] text-[#A1A1AA] whitespace-pre-wrap break-words">{it.prompt || "—"}</p>
                      </div>
                      <div>
                        <div className="font-mono text-[9px] uppercase tracking-wider text-[#52525B] mb-1">
                          Output <span className="text-[#52525B] normal-case">· ~{(it.tokens_in + it.tokens_out).toLocaleString()} tok</span>
                        </div>
                        <pre className="text-[11px] text-[#A1A1AA] bg-[#050505] border border-[#27272A] p-3 overflow-x-auto max-h-56 whitespace-pre-wrap break-words">{it.output || "—"}</pre>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
